import type { ArtifactFile } from '@shared/types';

export function getConversationAssetRoot(
  userId: string,
  conversationId: string,
) {
  return `users/${userId}/conversations/${conversationId}`;
}

export function getConversationImagePath(
  userId: string,
  conversationId: string,
  imageId: string,
) {
  return `${getConversationAssetRoot(userId, conversationId)}/images/${imageId}`;
}

export function getConversationSurfaceImagePath(
  userId: string,
  conversationId: string,
  imageId: string,
  extension = 'png',
) {
  return `${getConversationAssetRoot(userId, conversationId)}/surface-images/${imageId}.${extension}`;
}

export function getConversationMeshPath(
  userId: string,
  conversationId: string,
  meshId: string,
  fileType: string,
) {
  return `${getConversationAssetRoot(userId, conversationId)}/meshes/${meshId}.${fileType}`;
}

export function getConversationMeshPreviewPath(
  userId: string,
  conversationId: string,
  meshId: string,
  fileType: string,
) {
  return `${getConversationAssetRoot(userId, conversationId)}/mesh-previews/${meshId}.${fileType}`;
}

export function getConversationMeshRenderPath(
  userId: string,
  conversationId: string,
  renderId: string,
) {
  return `${getConversationAssetRoot(userId, conversationId)}/mesh-renders/${renderId}`;
}

export function getConversationArtifactPath(
  userId: string,
  conversationId: string,
  messageId: string,
  fileType: string,
) {
  return `${getConversationAssetRoot(userId, conversationId)}/artifacts/${messageId}.${fileType}`;
}

/** Storage path for an artifact file, content-addressed by format and role.
 *  Format: {asset root}/artifacts/{messageId}.{format}.
 *  Mirrors `getConversationArtifactPath` but takes an `ArtifactFile['format']`
 *  for type-safety at the call sites. */
export function getArtifactFilePath(
  userId: string,
  conversationId: string,
  messageId: string,
  format: ArtifactFile['format'],
): string {
  return `${getConversationAssetRoot(userId, conversationId)}/artifacts/${messageId}.${format}`;
}

/** Content-Type lookup for storage uploads. */
export function contentTypeForFormat(format: ArtifactFile['format']): string {
  switch (format) {
    case 'stl':
      return 'model/stl';
    case '3mf':
      return 'model/3mf';
    case 'scad':
      return 'text/x-openscad';
    case 'svg':
      return 'image/svg+xml';
    case 'pdf':
      return 'application/pdf';
    case 'gltf':
      return 'model/gltf+json';
    case 'glb':
      return 'model/gltf-binary';
  }
}
